// Activity for the Admin Activity tab: logins (per user per day) and task counts.
// Only counts and dates are stored. No task contents are copied.
import { ref, get, set, update } from 'firebase/database';
import { database } from '../firebaseConfig';
import { dayKey } from './deviceUsage';

export const recordLogin = async (uid) => {
  if (!uid) return;
  try {
    const now = new Date();
    await update(ref(database, `users/${uid}/activity`), { lastLogin: now.toISOString() });
    await set(ref(database, `users/${uid}/activity/loginDays/${dayKey(now)}`), true);
  } catch (err) {
    console.error('Could not record login', err);
  }
};

const daysAgoKey = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return dayKey(d);
};

// Counts per user: logins and task activity over the last 30 days, plus open and overdue tasks on the deals they own.
// Overdue and open counts use the deal owner, since tasks are stored on the deal.
export const loadActivityRows = async () => {
  const [usersSnap, idxSnap] = await Promise.all([get(ref(database, 'users')), get(ref(database, 'transactionIndex'))]);
  const users = usersSnap.val() || {};
  const index = idxSnap.val() || {};
  const since = daysAgoKey(30);
  const today = dayKey();
  const stats = {};
  const ensure = (uid) => { stats[uid] = stats[uid] || { completed30: 0, open: 0, overdue: 0 }; return stats[uid]; };

  for (const [txId, entry] of Object.entries(index)) {
    const snap = await get(ref(database, `transactions/${txId}/tasks`));
    if (!snap.exists()) continue;
    for (const t of Object.values(snap.val() || {})) {
      if (!t) continue;
      if (t.status === 'completed') {
        const done = (t.completedAt || '').slice(0, 10);
        if (t.completedByUid && done >= since) ensure(t.completedByUid).completed30 += 1;
      } else if (entry.ownerUid) {
        const s = ensure(entry.ownerUid);
        s.open += 1;
        if (t.dueDate && t.dueDate < today) s.overdue += 1;
      }
    }
  }

  return Object.entries(users).map(([uid, u]) => {
    const loginDays = Object.keys(u?.activity?.loginDays || {}).filter(d => d >= since);
    const s = stats[uid] || { completed30: 0, open: 0, overdue: 0 };
    return {
      uid,
      name: u.displayName || u.email || 'Unnamed user',
      role: u.role || '',
      lastLogin: u?.activity?.lastLogin || null,
      loginDays30: loginDays.length,
      completed30: s.completed30,
      points: u?.gamification?.points || 0,
      streak: u?.gamification?.streak || 0,
      open: s.open,
      overdue: s.overdue,
    };
  }).sort((a, b) => a.name.localeCompare(b.name));
};
